import React, { useEffect, useState } from "react";
import { LogOut, Plus, Trash2, Inbox, BarChart3, RefreshCw, Sparkles, Receipt, ExternalLink, CheckCircle2 } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const empty = {
  target_name: "",
  impressions: 0,
  people_entered: 0,
  ctr: 0,
  date_start: "",
  date_end: "",
  notes: "",
};

export default function PortalPage() {
  const { user, logout } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, c] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/submissions"),
        api.get("/admin/campaign-requests"),
      ]);
      setMetrics(m.data);
      setSubs(s.data);
      setCampaigns(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submitMetric = async (e) => {
    e.preventDefault();
    if (!form.target_name || !form.date_start || !form.date_end) {
      toast.error("Target, start and end dates are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/metrics", {
        ...form,
        impressions: Number(form.impressions),
        people_entered: Number(form.people_entered),
        ctr: Number(form.ctr),
      });
      toast.success("Metric saved.");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteMetric = async (id) => {
    if (!window.confirm("Delete this metric?")) return;
    try {
      await api.delete(`/admin/metrics/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign request?")) return;
    try {
      await api.delete(`/admin/campaign-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const [invoicingId, setInvoicingId] = useState(null);
  const createInvoice = async (id) => {
    setInvoicingId(id);
    try {
      const { data } = await api.post(`/admin/campaign-requests/${id}/create-checkout`, {
        origin_url: window.location.origin,
      });
      toast.success("Invoice link ready. Opening Stripe…");
      try { await navigator.clipboard.writeText(data.url); toast.success("Link copied to clipboard."); } catch { /* ignore */ }
      window.open(data.url, "_blank", "noopener,noreferrer");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invoice creation failed");
    } finally {
      setInvoicingId(null);
    }
  };

  const openExistingInvoice = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <SiteShell>
      <section className="py-16" data-testid="portal-section">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="flex items-start justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="kicker mb-3">Client Portal</div>
              <h1 className="h-display text-white text-[36px] sm:text-[48px]">
                WELCOME, <span className="text-[#d4af37]">{user?.name?.toUpperCase() || "ADMIN"}.</span>
              </h1>
              <p className="text-white/55 text-[13px] mt-3 tracking-wide">{user?.email}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={load} className="btn-outline-gold btn-label" data-testid="portal-refresh-btn">
                <RefreshCw size={14} strokeWidth={1.6} /> Refresh
              </button>
              <button onClick={logout} className="btn-outline-gold btn-label" data-testid="portal-logout-btn">
                <LogOut size={14} strokeWidth={1.6} /> Log Out
              </button>
            </div>
          </div>

          <Tabs defaultValue="metrics" className="w-full">
            <TabsList className="bg-transparent border-b border-[#d4af37]/15 rounded-none p-0 h-auto gap-8 mb-10">
              <TabsTrigger
                value="metrics"
                className="data-[state=active]:bg-transparent data-[state=active]:text-[#d4af37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#d4af37] px-0 py-3 text-[12px] tracking-[0.22em] uppercase text-white/60"
                data-testid="tab-metrics"
              >
                <BarChart3 size={14} strokeWidth={1.4} className="mr-2" /> Campaign Metrics
              </TabsTrigger>
              <TabsTrigger
                value="submissions"
                className="data-[state=active]:bg-transparent data-[state=active]:text-[#d4af37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#d4af37] px-0 py-3 text-[12px] tracking-[0.22em] uppercase text-white/60"
                data-testid="tab-submissions"
              >
                <Inbox size={14} strokeWidth={1.4} className="mr-2" /> Submissions ({subs.length})
              </TabsTrigger>
              <TabsTrigger
                value="campaigns"
                className="data-[state=active]:bg-transparent data-[state=active]:text-[#d4af37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#d4af37] px-0 py-3 text-[12px] tracking-[0.22em] uppercase text-white/60"
                data-testid="tab-campaigns"
              >
                <Sparkles size={14} strokeWidth={1.4} className="mr-2" /> Campaign Requests ({campaigns.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="metrics" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Form */}
                <div className="lg:col-span-5">
                  <h3 className="kicker mb-6 text-[#d4af37]">Upload New Metric</h3>
                  <form onSubmit={submitMetric} className="space-y-5" data-testid="metric-form">
                    <PField label="Target Name *">
                      <input className="lux-input" value={form.target_name} onChange={onChange("target_name")} placeholder="Lumen Field" data-testid="metric-input-target" />
                    </PField>
                    <div className="grid grid-cols-2 gap-4">
                      <PField label="Impressions">
                        <input type="number" className="lux-input" value={form.impressions} onChange={onChange("impressions")} data-testid="metric-input-impressions" />
                      </PField>
                      <PField label="People Entered">
                        <input type="number" className="lux-input" value={form.people_entered} onChange={onChange("people_entered")} data-testid="metric-input-people" />
                      </PField>
                    </div>
                    <PField label="CTR (%)">
                      <input type="number" step="0.1" className="lux-input" value={form.ctr} onChange={onChange("ctr")} data-testid="metric-input-ctr" />
                    </PField>
                    <div className="grid grid-cols-2 gap-4">
                      <PField label="Start Date *">
                        <input type="date" className="lux-input" value={form.date_start} onChange={onChange("date_start")} data-testid="metric-input-start" />
                      </PField>
                      <PField label="End Date *">
                        <input type="date" className="lux-input" value={form.date_end} onChange={onChange("date_end")} data-testid="metric-input-end" />
                      </PField>
                    </div>
                    <PField label="Notes">
                      <textarea className="lux-input" rows={3} value={form.notes} onChange={onChange("notes")} data-testid="metric-input-notes" />
                    </PField>
                    <button type="submit" disabled={saving} className="btn-gold btn-label disabled:opacity-60" data-testid="metric-save-btn">
                      <Plus size={14} strokeWidth={1.6} /> {saving ? "Saving…" : "Save Metric"}
                    </button>
                  </form>
                </div>

                {/* List */}
                <div className="lg:col-span-7">
                  <h3 className="kicker mb-6 text-[#d4af37]">Uploaded Metrics ({metrics.length})</h3>
                  {loading ? (
                    <p className="text-white/50 text-[13px]">Loading…</p>
                  ) : metrics.length === 0 ? (
                    <p className="text-white/50 text-[13px]" data-testid="metrics-empty">No metrics uploaded yet.</p>
                  ) : (
                    <div className="space-y-3" data-testid="metrics-list">
                      {metrics.map((m) => (
                        <div key={m.id} className="bg-[#111111] border border-[#d4af37]/15 p-5 flex items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="kicker text-[#d4af37]">{m.target_name}</span>
                              <span className="text-white/40 text-[11px]">{m.date_start} → {m.date_end}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-3">
                              <Tiny label="Impressions" value={Number(m.impressions).toLocaleString()} />
                              <Tiny label="Entered" value={Number(m.people_entered).toLocaleString()} />
                              <Tiny label="CTR" value={`${m.ctr}%`} highlight />
                            </div>
                            {m.notes && <p className="text-white/50 text-[12px] mt-3">{m.notes}</p>}
                          </div>
                          <button onClick={() => deleteMetric(m.id)} className="text-white/40 hover:text-red-400" data-testid={`metric-delete-${m.id}`} aria-label="Delete">
                            <Trash2 size={16} strokeWidth={1.4} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="submissions" className="mt-0">
              {loading ? (
                <p className="text-white/50 text-[13px]">Loading…</p>
              ) : subs.length === 0 ? (
                <p className="text-white/50 text-[13px]" data-testid="subs-empty">No submissions yet.</p>
              ) : (
                <div className="space-y-3" data-testid="subs-list">
                  {subs.map((s) => (
                    <div key={s.id} className="bg-[#111111] border border-[#d4af37]/15 p-5">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="kicker text-[#d4af37]">{s.kind.replace("_", " ")}</span>
                          <span className="text-white/40 text-[11px]">{new Date(s.created_at).toLocaleString()}</span>
                        </div>
                        <button onClick={() => deleteSubmission(s.id)} className="text-white/40 hover:text-red-400" data-testid={`sub-delete-${s.id}`} aria-label="Delete">
                          <Trash2 size={16} strokeWidth={1.4} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
                        {Object.entries(s.data || {}).map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <span className="text-white/40 uppercase text-[10px] tracking-[0.2em] min-w-[90px] pt-1">{k}</span>
                            <span className="text-white/85 break-words">{String(v) || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="campaigns" className="mt-0">
              {loading ? (
                <p className="text-white/50 text-[13px]">Loading…</p>
              ) : campaigns.length === 0 ? (
                <p className="text-white/50 text-[13px]" data-testid="campaigns-empty">No campaign requests yet.</p>
              ) : (
                <div className="space-y-4" data-testid="campaigns-list">
                  {campaigns.map((c) => {
                    const p = c.payload || {};
                    const isPaid = c.payment_status === "paid";
                    const hasInvoice = !!c.stripe_checkout_url;
                    return (
                      <div key={c.id} className="bg-[#111111] border border-[#d4af37]/15 p-6">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="kicker text-[#d4af37]">{p.company || p.brand || "—"}</span>
                            <span className="text-white/40 text-[11px]">{new Date(c.created_at).toLocaleString()}</span>
                            {isPaid && (
                              <span className="text-[10px] tracking-[0.18em] uppercase border border-[#d4af37]/60 text-[#d4af37] px-2 py-0.5 flex items-center gap-1" data-testid={`paid-badge-${c.id}`}>
                                <CheckCircle2 size={10} /> Paid
                              </span>
                            )}
                            {!isPaid && hasInvoice && (
                              <span className="text-[10px] tracking-[0.18em] uppercase border border-amber-400/40 text-amber-300 px-2 py-0.5">Invoice Sent</span>
                            )}
                            {(c.sensitive_flags || []).map((f) => (
                              <span key={f} className="text-[10px] tracking-[0.18em] uppercase border border-amber-400/40 text-amber-300 px-2 py-0.5">{f}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-display text-[#d4af37] text-[22px]">${Math.round(c.total_cost).toLocaleString()}</span>
                            {!isPaid && !hasInvoice && (
                              <button onClick={() => createInvoice(c.id)} disabled={invoicingId === c.id} className="btn-gold btn-label" data-testid={`campaign-invoice-${c.id}`}>
                                {invoicingId === c.id ? "…" : <><Receipt size={13} strokeWidth={1.6} /> Create Invoice</>}
                              </button>
                            )}
                            {!isPaid && hasInvoice && (
                              <button onClick={() => openExistingInvoice(c.stripe_checkout_url)} className="btn-outline-gold btn-label" data-testid={`campaign-view-invoice-${c.id}`}>
                                <ExternalLink size={13} strokeWidth={1.6} /> View Invoice
                              </button>
                            )}
                            <button onClick={() => deleteCampaign(c.id)} className="text-white/40 hover:text-red-400" aria-label="Delete" data-testid={`campaign-delete-${c.id}`}>
                              <Trash2 size={16} strokeWidth={1.4} />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-[13px]">
                          <Detail label="Contact" value={`${p.name} • ${p.email}${p.phone ? ` • ${p.phone}` : ""}`} />
                          <Detail label="Industry" value={p.industry} />
                          <Detail label="Goal" value={p.primary_goal} />
                          <Detail label="Targeting" value={p.targeting_type} />
                          <Detail label="Impressions" value={Number(c.total_impressions).toLocaleString()} />
                          <Detail label="Budget" value={p.monthly_budget ? `$${Number(p.monthly_budget).toLocaleString()}` : "—"} />
                          <Detail label="Media Cost" value={c.media_cost ? `$${Number(c.media_cost).toLocaleString()}` : "—"} />
                          <Detail label="Setup Fee" value={c.setup_fee_waived ? "Waived" : `$${Number(c.setup_fee || 0).toLocaleString()}`} />
                          <Detail label="Total" value={`$${Number(c.total_cost).toLocaleString()}`} />
                        </div>
                        {p.formats && p.formats.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="kicker mb-2 text-[#d4af37]">Formats</div>
                            <ul className="text-[13px] text-white/80 space-y-1">
                              {p.formats.map((f) => (
                                <li key={f.format}>
                                  {f.format} — {Number(f.impressions).toLocaleString()} impressions
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {p.ai_summary && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="kicker mb-2 text-[#d4af37]">AI Brief</div>
                            <pre className="text-[12px] text-white/75 whitespace-pre-wrap font-body leading-relaxed">{p.ai_summary}</pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </SiteShell>
  );
}

function PField({ label, children }) {
  return (
    <label className="block">
      <span className="kicker mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-1">{label}</div>
      <div className="text-white/85 text-[13px]">{value || "—"}</div>
    </div>
  );
}

function Tiny({ label, value, highlight }) {
  return (
    <div>
      <div className="text-white/40 text-[10px] tracking-[0.22em] uppercase">{label}</div>
      <div className={`font-display text-[18px] ${highlight ? "text-[#d4af37]" : "text-white"}`}>{value}</div>
    </div>
  );
}
